import React, { FC } from "react"
import { Link } from "react-router-dom"

import { ActionIcon, Button, Card, Grid, Group, Menu, Text } from "@mantine/core"
import { IconArrowRight, IconDotsVertical, IconListDetails, IconMapPin } from "@tabler/icons-react"
import { useDisclosure } from "@mantine/hooks"

import ModulesSummary from "../services-summary/ModulesSummary"
import BatteryStatus from "../battery-status/BatteryStatus"
import DeviceMap from "./DeviceMap"
import useDeviceDetails from "./useDeviceDetails"
import { DeviceDocument, useGroups } from "../../../../contexts/devices-context"
import { useLanguage } from "../../../../contexts/language-context"
import ChangeGroupModal from "../change-group-modal/ChangeGroupModal"
import RemoveFromGroupModal from "../remove-from-group-modal/RemoveFromGroupModal"
import DeviceStatusBadge from "../device-status-badge/DeviceStatusBadge"
import RequiresRole from "../../../auth/components/require-auth/RequiresRole"

type DeviceDetailsCardProps = {
    device: DeviceDocument
}

const DeviceDetailsCard: FC<DeviceDetailsCardProps> = ({ device }) => {
    const { form, onSubmit, edit, open, cancel, setCoordinates, reset, processing } = useDeviceDetails(device)
    const { map, organisation } = useGroups()
    const [changeGroupOpened, { open: openChangeGroup, close: closeChangeGroup }] = useDisclosure(false)
    const [removeFromGroupOpened, { open: openRemoveFromGroup, close: closeRemoveFromGroup }] = useDisclosure(false)
    const { L } = useLanguage()

    return (
        <Card withBorder>
            <form onSubmit={form.onSubmit(onSubmit)}>
                <Grid>
                    <Grid.Col span={12} lg={7}>
                        <Grid mr={{ base: "-0.5rem", lg: "0.5rem" }} mb={{ base: "0.5rem", lg: "-0.5rem" }}>
                            <Grid.Col span={12}>
                                <Group position="apart" noWrap>
                                    <Text
                                        color="dimmed"
                                        weight="bold"
                                        sx={theme => ({ fontFamily: theme.headings.fontFamily })}
                                    >
                                        <Group spacing="xs">
                                            <IconListDetails size={20} />
                                            {L("Details")}
                                        </Group>
                                    </Text>
                                    <RequiresRole role="edit-devices">
                                        <Menu shadow="lg" withinPortal width={240}>
                                            <Menu.Target>
                                                <ActionIcon size={32} variant="transparent">
                                                    <IconDotsVertical size={24} />
                                                </ActionIcon>
                                            </Menu.Target>
                                            <Menu.Dropdown>
                                                <Menu.Item
                                                    icon={<IconMapPin size={16} />}
                                                    onClick={open}
                                                    disabled={processing}
                                                >
                                                    {L("Set location")}
                                                </Menu.Item>
                                                <Menu.Divider />
                                                <Menu.Label>{L("Groups")}</Menu.Label>
                                                <Menu.Item
                                                    rightSection={<IconArrowRight size={14} />}
                                                    component={Link}
                                                    to={`/groups/${device.group}`}
                                                >
                                                    {L("View group")}
                                                </Menu.Item>
                                                <Menu.Item
                                                    rightSection={<IconArrowRight size={14} />}
                                                    onClick={openChangeGroup}
                                                >
                                                    {device.group === device.organisation
                                                        ? L("Add to group")
                                                        : L("Change group")}
                                                </Menu.Item>
                                                <Menu.Item
                                                    color="red"
                                                    rightSection={<IconArrowRight size={14} />}
                                                    onClick={openRemoveFromGroup}
                                                    disabled={device.group === device.organisation}
                                                >
                                                    {L("Remove from group")}
                                                </Menu.Item>
                                            </Menu.Dropdown>
                                            <ChangeGroupModal
                                                opened={changeGroupOpened}
                                                onClose={closeChangeGroup}
                                                device={device}
                                            />
                                            {device.group === device.organisation || (
                                                <RemoveFromGroupModal
                                                    opened={removeFromGroupOpened}
                                                    onClose={closeRemoveFromGroup}
                                                    device={device}
                                                />
                                            )}
                                        </Menu>
                                    </RequiresRole>
                                </Group>
                            </Grid.Col>
                            <Grid.Col span={4} md={2}>
                                <Text color="dimmed">{L("Name")}</Text>
                            </Grid.Col>
                            <Grid.Col span={8} md={4}>
                                <Text>{device.name}</Text>
                            </Grid.Col>
                            <Grid.Col span={4} md={2}>
                                <Text color="dimmed">{L("Status")}</Text>
                            </Grid.Col>
                            <Grid.Col span={8} md={4}>
                                <DeviceStatusBadge health={device.health} />
                            </Grid.Col>
                            <Grid.Col span={4} md={2}>
                                <Text color="dimmed">{L("Group")}</Text>
                            </Grid.Col>
                            <Grid.Col span={8} md={4}>
                                <Text>{device.group in map ? map[device.group].name : L("Without group")}</Text>
                            </Grid.Col>
                            <Grid.Col span={4} md={2}>
                                <Text color="dimmed">{L("Modules")}</Text>
                            </Grid.Col>
                            <Grid.Col span={8} md={4}>
                                <ModulesSummary modules={device.modules} />
                            </Grid.Col>
                            <Grid.Col span={4} md={2}>
                                <Text color="dimmed">{L("Organisation")}</Text>
                            </Grid.Col>
                            <Grid.Col span={8} md={4}>
                                <Text>{organisation?.name}</Text>
                            </Grid.Col>
                            {typeof device.health?.battery === "number" && (
                                <>
                                    <Grid.Col span={4} md={2}>
                                        <Text color="dimmed">
                                            {L("Battery")}
                                        </Text>
                                    </Grid.Col>
                                    <Grid.Col span={6} md={4}>
                                        <BatteryStatus
                                            value={device.health.battery}
                                            charging={device.health.charging}
                                        />
                                    </Grid.Col>
                                </>
                            )}
                            <Grid.Col span={4} md={2} sx={{ alignSelf: "start" }}>
                                <Text color="dimmed">{L("Model")}</Text>
                            </Grid.Col>
                            <Grid.Col span={8} md={4}>
                                <Text>{device.type}</Text>
                            </Grid.Col>
                            {device.health?.version && (
                                <>
                                    <Grid.Col span={4} md={2} sx={{ alignSelf: "start" }}>
                                        <Text color="dimmed">{L("Version")}</Text>
                                    </Grid.Col>
                                    <Grid.Col span={8} md={4}>
                                        <Text>{device.health.version}</Text>
                                    </Grid.Col>
                                </>
                            )}
                            {device.health?.network && (
                                <>
                                    <Grid.Col span={4} md={2} sx={{ alignSelf: "start" }}>
                                        <Text color="dimmed">{L("Connection")}</Text>
                                    </Grid.Col>
                                    <Grid.Col span={8} md={4}>
                                        <Text>{device.health.network}</Text>
                                    </Grid.Col>
                                </>
                            )}
                            {edit && (
                                <Grid.Col span={12}>
                                    <Group position="right" noWrap>
                                        <Button variant="subtle" onClick={cancel}>
                                            {L("Cancel")}
                                        </Button>
                                        <Button variant="subtle" onClick={reset} disabled={!form.isDirty()}>
                                            {L("Reset")}
                                        </Button>
                                        <Button type="submit" disabled={!form.isDirty() || !form.isValid()}>
                                            {L("Save")}
                                        </Button>
                                    </Group>
                                </Grid.Col>
                            )}
                        </Grid>
                    </Grid.Col>
                    <Grid.Col span={12} lg={5}>
                        <Card h="calc(100% + 2rem)" mih="35vh" m="-md" p={0} shadow="">
                            <DeviceMap
                                markers={
                                    form.values.latitude && form.values.longitude
                                        ? [
                                              {
                                                  label: device.name,
                                                  latitude: form.values.latitude,
                                                  longitude: form.values.longitude,
                                              },
                                          ]
                                        : []
                                }
                                readOnly={!edit}
                                onClick={setCoordinates}
                            />
                        </Card>
                    </Grid.Col>
                </Grid>
            </form>
        </Card>
    )
}

export default DeviceDetailsCard
